import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as AuthActions from './auth.actions';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { globalFailure, noopAction } from './../../../app/@store/common.actions';
import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';
import { getAuth, isLoggedIn } from './auth.selectors';
import { LoginResult } from './../../../app/@shared/models/login_result.model';
import { LocalstorageService } from '@gridscale/gs-services/localstorage.service';
import { ConfigService } from '@gridscale/ingrid/helper/services/config.service';

@Injectable()
export class AuthEffects {
  readonly #configService = inject(ConfigService);
  private current_endpoint = this.#configService.coreAPI;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly localStorage: LocalstorageService,
    private http: HttpClient
  ) {

    this.#configService.ready$.pipe(
      filter(ready => !!ready),
    ).subscribe(() => {
      this.current_endpoint = this.#configService.coreAPI;
    })

  }

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      concatMap(action =>
        this.http.post<{ body: Partial<LoginResult> }>(this.#configService.partnerLegacyApi + '/api/v1/login', action.data).pipe(
          map(loginResult => {
            this.localStorage.setItem('gs_pp_auth', JSON.stringify(loginResult.body));

            setTimeout(() => {
              window.dispatchEvent(new Event('storage'));
            }, 100);

            return AuthActions.loginSuccess({
              data: loginResult.body
            });
          }),
          catchError(error => of(AuthActions.loginFailure({ error }), globalFailure({ error }))),
        )
      ),
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      withLatestFrom(this.store.select(getAuth)),
      map(([action, auth]) => {
        this.localStorage.removeItem('gs_pp_auth');
        window.location.href = '/';
        return AuthActions.logoutSuccess();
      })
    )
  );

  reValidateAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.reValidateAuth),
      withLatestFrom(this.store.select(getAuth)),
      filter(([action, auth]) => auth !== undefined),
      map(([action, auth]) => auth as { token: string; user_uuid: string; }),
      map(auth => {
        return AuthActions.validateAuth({ data: auth });
      })
    )
  );

  validateAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateAuth, AuthActions.loginSuccess),
      withLatestFrom(this.store.select(isLoggedIn), this.#configService.ready$),
      filter(([action, isLoggedIn, configReady]) => !!configReady),
      concatMap(([action, isLoggedIn]) => {

        return this.http
          .get(this.current_endpoint + '/validate_token', {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'x-api-client': 'partnerpanel2',
              'X-Auth-UserId': action.data.user_uuid as string,
              'X-Auth-Token': action.data.token as string
            },
            withCredentials: false
          })
          .pipe(
            map((result: any) => {
              // if we are logged in, we only fire a validateAuthSuccess but if that was the first time we validated, we do a login success to trigger all the Default Loaders
              if (isLoggedIn) {
                return AuthActions.validateAuthSuccess({
                  data: {
                    token: action.data.token as string,
                    user_uuid: action.data.user_uuid as string
                  }
                });
              } else {
                return AuthActions.loginSuccess({
                  data: action.data
                });
              }
            }),
            catchError(error => {
              if (error.status === 0) {
                // network errors should not lead to logout
                return of(noopAction());
              }
              return of(AuthActions.logout(), AuthActions.validateAuthFailure({ error }), globalFailure({ error }))
            })
          ) as Observable<any>;
      })
    )
  );
}
